import { useAuth0 } from '@auth0/auth0-react';
import { Button, Outline, SelectionList, SelectionListItem } from '@ths/ui';
import { useEffect, useRef } from 'react';

import Loading from '../components/Loading';
import { ClientPod, useHotelMembershipsQuery } from '../generated/graphql';
import { getContactEmailByPod } from '../utilities/getContactEmailByPod';

import { getRememberedClient } from './rememberedClient';

type CompanySelectorProps = {
  currentClientId?: string;
  onSelect: (clientId: string) => void;
  onCancel?: () => void;
  showIntroduction?: boolean;
};

export function CompanySelector({
  currentClientId,
  onSelect,
  onCancel,
  showIntroduction = false,
}: CompanySelectorProps) {
  const { user } = useAuth0();
  const { data, loading, error, refetch } = useHotelMembershipsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const selected = currentClientId ?? getRememberedClient(user?.sub);
  const memberships = data?.hotelMemberships ?? [];
  const onlyClientId =
    memberships.length === 1 ? memberships[0].clientId : null;
  const autoSelectedClientId = useRef<string | null>(null);

  useEffect(() => {
    if (loading || error) return;
    if (!onlyClientId) {
      autoSelectedClientId.current = null;
      return;
    }
    if (autoSelectedClientId.current === onlyClientId) return;
    autoSelectedClientId.current = onlyClientId;
    onSelect(onlyClientId);
  }, [error, loading, onSelect, onlyClientId]);

  return (
    <>
      {showIntroduction && !loading && !error && memberships.length > 1 && (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">
            Choose a company
          </h1>
          <p className="mb-6 mt-2 text-text-secondary">
            You’ll see its trips, reservations, and requests.
          </p>
        </>
      )}
      {loading ? (
        <Loading />
      ) : error ? (
        <div className="space-y-4">
          <p role="alert">Company access could not be loaded.</p>
          <Button onClick={() => void refetch()}>Try again</Button>
        </div>
      ) : memberships.length === 0 ? (
        <div className="space-y-4">
          <p>
            No companies are assigned to your account. Contact THS to request
            access.
          </p>
          <Button
            as="a"
            {...{ href: `mailto:${getContactEmailByPod(ClientPod.Other)}` }}
          >
            Contact THS
          </Button>
        </div>
      ) : onlyClientId ? (
        <Loading />
      ) : (
        <SelectionList
          aria-label="Available companies"
          className="max-h-80 space-y-0 divide-y divide-separator overflow-y-auto rounded-md border border-separator"
        >
          {memberships.map((item) => (
            <SelectionListItem
              key={item.clientId}
              title={
                <span className="font-medium [overflow-wrap:anywhere]">
                  {item.clientName}
                </span>
              }
              secondary={
                item.isInactive
                  ? 'Inactive · Existing reservations only'
                  : undefined
              }
              selected={selected === item.clientId}
              aria-pressed={selected === item.clientId}
              truncate={false}
              className="min-h-16 rounded-none px-4 focus-visible:ring-inset focus-visible:ring-offset-0"
              trailing={
                selected === item.clientId ? (
                  <Outline.CheckIcon
                    aria-hidden="true"
                    className="size-5 text-accent"
                  />
                ) : undefined
              }
              onClick={() => onSelect(item.clientId)}
            />
          ))}
        </SelectionList>
      )}
      {onCancel && (
        <div className="mt-6">
          <Button fullWidth variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </>
  );
}
